/*
 source: http://fabricjs.com/custom-filter-swap-color
 */
const filter = (fabric) => {

    const filters = fabric.Image.filters;
    const createClass = fabric.util.createClass;

    /**
     * SwapColor filter class
     * @class fabric.Image.filters.SwapColor
     * @memberOf fabric.Image.filters
     * @extends fabric.Image.filters.BaseFilter
     * @see {@link fabric.Image.filters.SwapColor#initialize} for constructor definition
     * @example
     * var filter = new fabric.Image.filters.SwapColor({
     *   colorSource: 'orange',
     *   colorDestination: 'rgb(232, 12, 11)',
     * });
     * object.filters.push(filter);
     * object.applyFilters();
     */
    filters.SwapColor = createClass(filters.BaseFilter, /** @lends fabric.Image.filters.SwapColor.prototype */ {

        /**
         * Filter type
         * @param {String} type
         * @default
         */
        type: 'SwapColor',

        /**
         * Fragment source for the SwapColor program
         *
         * INFO: updated with chatGPT, gives better results but still has artifacts most likely because of PNG compression
         *       to fix we disable full webGL via fabric.enableGLFiltering = false;
         */
        fragmentSource: `
            precision highp float;
            uniform sampler2D uTexture;
            uniform vec4 colorSource;
            uniform vec4 colorDestination;
            uniform float tolerance;
            varying vec2 vTexCoord;

            void main() {
                vec4 color = texture2D(uTexture, vTexCoord);
                vec3 sourceRGB = colorSource.rgb;
                vec3 destRGB = colorDestination.rgb;

                float colorDistance = length(sourceRGB - color.rgb);
                bool colorMatch = colorDistance < tolerance && color.a > 0.0;

                // Adjust the tolerance for alpha channel
                if (colorMatch && abs(colorSource.a - color.a) > 0.1) {
                    colorMatch = false;
                }

                gl_FragColor = colorMatch ? vec4(destRGB, color.a) : color;
            }
        `,

        /**
         * SwapColor colorSource, a css color
         * @param {String} colorSource
         * @default
         */
        colorSource: 'rgb(255, 0, 0)',

        /**
         * SwapColor colorSource, a css color
         * @param {String} colorDestination
         * @default
         */
        colorDestination: 'rgb(0, 255, 0)',

        /**
         * SwapColor tolerance
         */
        tolerance: 0.1,

        /**
         * Apply the SwapColor operation to a Uint8ClampedArray representing the pixels of an image.
         *
         * @param {Object} options
         * @param {ImageData} options.imageData The Uint8ClampedArray to be filtered.
         */
        applyTo2d: function(options) {

            var imageData = options.imageData,
                data = imageData.data,
                i, len = data.length,
                source = new fabric.Color(this.colorSource).getSource(),
                destination = new fabric.Color(this.colorDestination).getSource();

            // original code without tolerance (faster and default is 0.1)
            if(this.tolerance <= 0.1){

                for (i = 0; i < len; i += 4) {
                    if (data[i] === source[0] && data[i + 1] === source[1] && data[i + 2] === source[2]) {
                        data[i] = destination[0];
                        data[i + 1] = destination[1];
                        data[i + 2] = destination[2];
                    }
                }

            } else {
                for (i = 0; i < len; i += 4) {
                    var pixelR = data[i];
                    var pixelG = data[i + 1];
                    var pixelB = data[i + 2];
                    var pixelA = data[i + 3];

                    // Check for color match with tolerance
                    var colorDistance = Math.sqrt(
                        Math.pow(source[0] - pixelR, 2) +
                        Math.pow(source[1] - pixelG, 2) +
                        Math.pow(source[2] - pixelB, 2)
                    );

                    if (colorDistance < this.tolerance && pixelA > 0) {
                        data[i] = destination[0];
                        data[i + 1] = destination[1];
                        data[i + 2] = destination[2];
                    }
                }
            }

        },

        /**
         * Return WebGL uniform locations for this filter's shader.
         *
         * @param {WebGLRenderingContext} gl The GL canvas context used to compile this filter's shader.
         * @param {WebGLShaderProgram} program This filter's compiled shader program.
         */
        getUniformLocations: function(gl, program) {
            return {
                uColorSource: gl.getUniformLocation(program, 'colorSource'),
                uColorDestination: gl.getUniformLocation(program, 'colorDestination'),
                uTolerance: gl.getUniformLocation(program, 'tolerance'),
            };
        },

        /**
         * Send data from this filter to its shader program's uniforms.
         *
         * @param {WebGLRenderingContext} gl The GL canvas context used to compile this filter's shader.
         * @param {Object} uniformLocations A map of string uniform names to WebGLUniformLocation objects
         */
        sendUniformData: function(gl, uniformLocations) {
            var source = new fabric.Color(this.colorSource).getSource();
            var destination = new fabric.Color(this.colorDestination).getSource();

            source[0] /= 255;
            source[1] /= 255;
            source[2] /= 255;

            destination[0] /= 255;
            destination[1] /= 255;
            destination[2] /= 255;

            gl.uniform4fv(uniformLocations.uColorSource, source);
            gl.uniform4fv(uniformLocations.uColorDestination, destination);
            gl.uniform1f(uniformLocations.uTolerance, this.tolerance);
        },

        isNeutralState: function() {
            return this.colorSource === this.colorDestination;
        },

        /**
         * Returns object representation of an instance
         * @return {Object} Object representation of an instance
         */
        toObject: function() {
            return fabric.util.object.extend(this.callSuper('toObject'), {
                colorSource: this.colorSource,
                colorDestination: this.colorDestination,
            });
        }
    });

    /**
     * Returns filter instance from an object representation
     * @static
     * @param {Object} object Object to create an instance from
     * @param {function} [callback] to be invoked after filter creation
     * @return {fabric.Image.filters.SwapColor} Instance of fabric.Image.filters.SwapColor
     */
    fabric.Image.filters.SwapColor.fromObject = fabric.Image.filters.BaseFilter.fromObject;

};

export default filter;